import { useState } from "react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import { Input } from "@monorepo-apps/ui/src/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import { DOMAIN_LABEL, DOMAINS, type Domain } from "@/presentation/data/domains";
import { ROADMAP_LABEL, type RoadmapBranch } from "@/presentation/data/roadmaps";

export interface CreateFeatureFormValues {
  title: string;
  subtitle: string | null;
  description: string | null;
  domain: Domain | null;
  component_id: string | null;
  journey: "B2C" | "B2B" | "both" | null;
}

interface CreateFeatureDialogProps {
  open: boolean;
  branch: RoadmapBranch;
  isSubmitting: boolean;
  onSubmit: (values: CreateFeatureFormValues) => void;
  onClose: () => void;
  /** When provided, pre-fills and locks the domain select. */
  defaultDomain?: Domain | null;
  /** When provided, pre-fills the bound component id (domain must also be locked). */
  defaultComponentId?: string | null;
  /** Human-readable name of the bound component, shown as a hint. */
  defaultComponentLabel?: string | null;
}

const JOURNEY_OPTIONS: ReadonlyArray<{ value: "B2C" | "B2B" | "both"; label: string }> = [
  { value: "B2B", label: "B2B" },
  { value: "B2C", label: "B2C" },
  { value: "both", label: "Both" },
];

export function CreateFeatureDialog({
  open,
  branch,
  isSubmitting,
  onSubmit,
  onClose,
  defaultDomain,
  defaultComponentId,
  defaultComponentLabel,
}: CreateFeatureDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        {open ? (
          <CreateFeatureBody
            key={`${branch}-${defaultDomain ?? ""}-${defaultComponentId ?? ""}`}
            branch={branch}
            isSubmitting={isSubmitting}
            onSubmit={onSubmit}
            onClose={onClose}
            defaultDomain={defaultDomain ?? null}
            defaultComponentId={defaultComponentId ?? null}
            defaultComponentLabel={defaultComponentLabel ?? null}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function CreateFeatureBody({
  branch,
  isSubmitting,
  onSubmit,
  onClose,
  defaultDomain,
  defaultComponentId,
  defaultComponentLabel,
}: {
  branch: RoadmapBranch;
  isSubmitting: boolean;
  onSubmit: (values: CreateFeatureFormValues) => void;
  onClose: () => void;
  defaultDomain: Domain | null;
  defaultComponentId: string | null;
  defaultComponentLabel: string | null;
}) {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [domain, setDomain] = useState<Domain | "">(defaultDomain ?? "");
  const [journey, setJourney] = useState<"B2C" | "B2B" | "both" | "">("");

  const domainLocked = defaultDomain !== null;
  const canSubmit = title.trim().length > 0 && !isSubmitting;
  const destination = branch === "all-features" ? "All Features stack" : "MVP board stack";

  return (
    <>
      <DialogHeader>
        <DialogTitle>New feature</DialogTitle>
        <DialogDescription>
          Lands in the {destination}.
          {branch === "from-scratch"
            ? " Also marked MVP on the All Features board."
            : " Triage it into MVP or Non-MVP by dragging."}
        </DialogDescription>
      </DialogHeader>
      {defaultComponentLabel ? (
        <p className="text-foreground/70 -mt-1 text-[11.5px]">
          Will be bound to <span className="font-semibold">{defaultComponentLabel}</span>.
        </p>
      ) : null}
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          onSubmit({
            title: title.trim(),
            subtitle: subtitle.trim() ? subtitle.trim() : null,
            description: description.trim() ? description.trim() : null,
            domain: domain === "" ? null : domain,
            component_id: defaultComponentId,
            journey: journey === "" ? null : journey,
          });
        }}
      >
        <FormField label="Title" required>
          <Input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. B2B credit-limit enforcement at checkout"
          />
        </FormField>
        <FormField label="Subtitle">
          <Input
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            placeholder="Optional one-liner"
          />
        </FormField>
        <FormField label="Description">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Optional context — what this feature does, why it matters."
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Domain">
            <Select
              value={domain}
              onValueChange={(v) => setDomain(v as Domain)}
              disabled={domainLocked}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick a domain" />
              </SelectTrigger>
              <SelectContent>
                {DOMAINS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {DOMAIN_LABEL[d]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Journey">
            <Select value={journey} onValueChange={(v) => setJourney(v as "B2C" | "B2B" | "both")}>
              <SelectTrigger>
                <SelectValue placeholder="Pick a journey" />
              </SelectTrigger>
              <SelectContent>
                {JOURNEY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isSubmitting ? "Creating…" : `Create in ${ROADMAP_LABEL[branch]}`}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div data-slot="create-feature-field" className="space-y-1.5">
      <label className="text-foreground text-xs font-medium">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </label>
      {children}
    </div>
  );
}
